// What the layered rider and parked bike draw, back to front, as plain data (bike plan 3, task 2): the game's
// BikeSprite and the tests share it. Everything comes from bike_rig.json (scripts/anim/bike/export.py): the layer
// orders per row, the wheels, the designs exported. A layer is {atlas, frame}; the slot the spoke discs go in is a
// {discs: true} marker, and the discs themselves come from wheelsFor.
import { ROWS, rowFlipFromDir8 } from './dir8.mjs';
import { underTheHair } from './clothSlots.mjs';

// The ride's clothing order, both riders (garments.RIDE_Z_ORDERS; owner G7 2026-10-22: shoes under trousers).
export const RIDE_ORDER = ['shoes', 'bottom', 'top', 'jacket', 'accessory'];
const LEG_SLOTS = new Set(['shoes', 'bottom']);

export function viewFlip(dir8) {
  const { row, flip } = rowFlipFromDir8(dir8);
  return { view: ROWS[row], flip };
}

// the row a character has drawn, else three-quarter front (dev only: the ship gate needs every row)
export function rowFor(rig, char, view) {
  return rig.rows?.[char]?.includes(view) ? view : 'front34';
}

// Every design has its own atlas, the default too (`bike_pink_rozowy`): the bare `bike_pink` key is the shop's
// static picture. The default's atlas also holds the discs and the kickstand every design shares.
export function baseBikeAtlas(rig, type) {
  return `bike_${type}_${rig.designs?.[type]?.default}`;
}

function bikeAtlas(rig, type, design) {
  return design ? `bike_${type}_${design}` : baseBikeAtlas(rig, type);
}

// a bike part from its design's atlas, or the default's where the design lacks it
function bikePart(rig, type, design, view, part, has) {
  const own = { atlas: bikeAtlas(rig, type, design), frame: `${view}/${part}` };
  if (has(own.atlas, own.frame)) return own;
  const base = { atlas: baseBikeAtlas(rig, type), frame: own.frame };
  return has(base.atlas, base.frame) ? base : null;
}

function inOrder(items) {
  return [...items].sort((a, b) => RIDE_ORDER.indexOf(a.slot) - RIDE_ORDER.indexOf(b.slot));
}

// The ridden picture: the rig's order for the row, each body part followed by the clothes on it in the ride's
// order (a leg item's far_k / near_k / upper_k, an upper item's upper); whatever `has` says is not there is left out.
// `hair` ({front, back, neck} atlas keys, look/hairArt.mjs hairKeys('bike', …)): its '<view>/back' frame right before
// the upper body, the default's '<view>/neck' right after it (under the clothes), its '<view>/upper' after the clothes
// and under the accessory. On a mirrored row (`flip`) a one-sided style draws its own right-facing hair where its atlas
// has it: the '<view>_r/…' frames, stored mirrored back, so the flipped rider shows them facing right. `lift`
// ({itemId: canvas px}, look/hairArt.mjs itemLift) draws an item that much higher: its layer's `dy`.
export function riderLayers({ rig, char, view, ridden, type, design, items = [], hair = null, has, k = 0, flip = false, lift = {} }) {
  const order = rig.views[view][ridden ? 'orderRidden' : 'order'];
  const body = `bike_rider_${char}`;
  const worn = inOrder(items);
  const out = [];
  const push = (atlas, frame, up = 0) => { if (has(atlas, frame)) out.push(up ? { atlas, frame, dy: -up } : { atlas, frame }); };
  const hv = flip && hair && has(hair.front, `${view}_r/upper`) ? `${view}_r` : view;
  for (const part of order) {
    if (part === 'discs') { out.push({ discs: true }); continue; }
    if (part === 'far' || part === 'near') {
      push(body, `${view}/${part}_${k}`);
      for (const it of worn) if (LEG_SLOTS.has(it.slot)) push(`bike_cloth_${char}_${it.id}`, `${view}/${part}_${k}`);
      continue;
    }
    if (part === 'upper') {
      if (hair) push(hair.back, `${hv}/back`);
      push(body, `${view}/upper`);
      if (hair?.neck) push(hair.neck, `${view}/neck`);
      let front = !hair;
      for (const it of worn) {
        if (!front && it.slot === 'accessory' && !underTheHair(it.id, view)) { push(hair.front, `${hv}/upper`); front = true; }
        push(`bike_cloth_${char}_${it.id}`, LEG_SLOTS.has(it.slot) ? `${view}/upper_${k}` : `${view}/upper`, lift[it.id]);
      }
      if (!front) push(hair.front, `${hv}/upper`);
      continue;
    }
    const layer = bikePart(rig, type, design, view, part, has);
    if (layer) out.push(layer);
  }
  return out;
}

// A parked bike: no rider, on its kickstand (every design shares the default's kickstand).
export function parkedLayers({ rig, type, design, view, has }) {
  const out = [];
  for (const part of rig.views[view].orderParked) {
    if (part === 'discs') { out.push({ discs: true }); continue; }
    const layer = part === 'kickstand'
      ? (has(baseBikeAtlas(rig, type), `${view}/kickstand`) ? { atlas: baseBikeAtlas(rig, type), frame: `${view}/kickstand` } : null)
      : bikePart(rig, type, design, view, part, has);
    if (layer) out.push(layer);
  }
  return out;
}

// Where the character stands after hopping off (bike plan 3, task 7), from the bike's ground point: 0.35 of the
// rider's height square to the way the bike points, on its near-leg side, which is the camera's (toward the viewer).
// Head-on (S / N) both legs are as near: she steps off on the side of the planted left foot (screen right from the
// front, screen left from behind).
export const DISMOUNT_SIDE = 0.35;

export function dismountOffset(dir8, riderH) {
  const a = (((dir8 % 8) + 8) % 8) * Math.PI / 4;
  const vx = Math.cos(a), vy = Math.sin(a);
  let px = -vy, py = vx;
  if (Math.abs(py) < 1e-9) { px = Math.sign(vy); py = 0; }   // head-on: the planted foot's side
  else if (py < 0) { px = -px; py = -py; }
  const d = DISMOUNT_SIDE * riderH;
  return { dx: px * d, dy: py * d };
}

// …unless an obstacle or the scene's edge is there (ok(dx, dy): may her feet stand at that offset?): then the far
// side, and failing both, where the bike stands.
export function dismountSpot(dir8, riderH, ok) {
  const near = dismountOffset(dir8, riderH);
  for (const o of [near, { dx: -near.dx, dy: -near.dy }]) if (ok(o.dx, o.dy)) return o;
  return { dx: 0, dy: 0 };
}

// The spoke discs of a row: centre relative to the ground point, the ellipse's angle (rad) and its squash.
export function wheelsFor(rig, type, view) {
  const [gx, gy] = rig.ground;
  return (rig.wheels?.[type]?.[view] ?? []).map((w) => ({
    atlas: baseBikeAtlas(rig, type), frame: w.frame, x: w.center[0] - gx, y: w.center[1] - gy,
    angle: w.angleDeg * Math.PI / 180, ratio: w.axes[1] / w.axes[0],
  }));
}
